import { useState } from 'react';
import type { ReactNode } from 'react';

import {
  Box,
  Checkbox,
  CircularProgress,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import type { FormLabelProps } from '@mui/material';
import { SearchIcon } from '@shared/ui/icons';
import { Pagination } from '@shared/ui/pagination';

import { CollapseFilter } from '../collapse-filter';

import { getPage } from './lib';

type ListFilterProps<Data> = {
  title: string;
  options: Data[];
  value: string[];
  onChange: (value: string[]) => void;
  getKey: (option: Data) => string;
  getValue: (option: Data) => string;
  getLabel: (option: Data) => string;
  renderLabel?: (option: Data) => ReactNode;
  renderIcon?: (option: Data) => ReactNode;
  searchable?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  isLoading?: boolean;
  defaultOpen?: boolean;
  formLabelProps?: FormLabelProps;
  customInputPlaceholder?: string;
};

export function ListFilter<Data>(props: ListFilterProps<Data>) {
  const {
    title,
    options,
    value,
    onChange,
    getKey,
    getValue,
    getLabel,
    renderLabel,
    renderIcon,
    searchable,
    searchValue = '',
    onSearchChange,
    isLoading,
    defaultOpen,
    formLabelProps,
    customInputPlaceholder,
  } = props;
  const [internalSearch, setInternalSearch] = useState('');
  const [page, setPage] = useState(1);

  const presetValues = options.map(getValue);
  const customValueFromProps = value.find((item) => !presetValues.includes(item));
  const [customDraft, setCustomDraft] = useState(
    customInputPlaceholder !== undefined ? (customValueFromProps ?? '') : '',
  );

  const isExternalSearch = onSearchChange !== undefined;
  const isLocalSearch = searchable && !isExternalSearch;
  const search = isExternalSearch ? searchValue : internalSearch;

  const filteredOptions = isLocalSearch
    ? options.filter((option) =>
        getLabel(option).toLowerCase().includes(search.toLowerCase()),
      )
    : options;

  const { pageCount, currentPage, pageItems } = getPage(filteredOptions, page);

  const handleSearchChange = (value: string) => {
    setPage(1);
    if (isExternalSearch) {
      onSearchChange(value);
    } else {
      setInternalSearch(value);
    }
  };

  const handleToggle = (optionValue: string) => {
    const isSelected = value.includes(optionValue);
    const next = isSelected
      ? value.filter((selectedValue) => selectedValue !== optionValue)
      : [...value, optionValue];
    onChange(next);
  };

  const handleCustomChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').replace(/^0+/, '');
    setCustomDraft(digits);
    const parsed = digits && Number(digits) > 0 ? digits : null;
    const presets = value.filter((item) => presetValues.includes(item));
    const isPresetDuplicate = parsed !== null && presetValues.includes(parsed);
    onChange(parsed !== null && !isPresetDuplicate ? [...presets, parsed] : presets);
  };

  return (
    <CollapseFilter
      defaultOpen={defaultOpen}
      title={title}
      formLabelProps={formLabelProps}
    >
      {searchable && (
        <TextField
          size="small"
          placeholder="Search..."
          value={search}
          onChange={(event) => handleSearchChange(event.target.value)}
          fullWidth
          sx={{ mb: 1 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      )}
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      ) : filteredOptions.length === 0 ? (
        <Typography variant="body2" color="text.secondary" py={1}>
          Нічого не знайдено
        </Typography>
      ) : (
        <>
          <List disablePadding>
            {pageItems.map((option) => (
              <ListItem key={getKey(option)} disablePadding>
                <ListItemButton
                  disableRipple
                  onClick={() => handleToggle(getValue(option))}
                  dense
                  sx={{
                    pl: 0,
                    pr: 1,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: 'transparent' },
                  }}
                >
                  {renderIcon && (
                    <ListItemIcon sx={{ minWidth: 36, mr: 1 }}>
                      {renderIcon(option)}
                    </ListItemIcon>
                  )}
                  {renderLabel ? (
                    <Box flex={1}>{renderLabel(option)}</Box>
                  ) : (
                    <ListItemText
                      primary={getLabel(option)}
                      slotProps={{ primary: { fontWeight: 500 } }}
                    />
                  )}
                  <Checkbox
                    edge="end"
                    checked={value.includes(getValue(option))}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {pageCount > 1 && (
            <Pagination
              compact
              count={pageCount}
              page={currentPage}
              onPageChange={setPage}
              canPreviousPage={currentPage > 1}
              canNextPage={currentPage < pageCount}
              containerProps={{ p: 0 }}
            />
          )}
        </>
      )}
      {customInputPlaceholder !== undefined && (
        <TextField
          placeholder={customInputPlaceholder}
          size="small"
          value={customDraft}
          onChange={(event) => handleCustomChange(event.target.value)}
          slotProps={{ htmlInput: { inputMode: 'numeric' } }}
          sx={{ mt: 1 }}
          fullWidth
        />
      )}
    </CollapseFilter>
  );
}
